import { Grid, Card, Skeleton, Typography, Box } from '@mui/material';
import { EmptyState } from '@components/DataTable';
import TalentCard from '@modules/talents/TalentCard';
import { useDrawer } from '@components/Sidebar/DrawerContext';
import { MultiSelectTalent } from './type';

interface TalentListGridProps {
    cardMenuList: {
        id: number;
        title: string;
        icon: React.ElementType;
        type: string;
        style?: string;
    }[];
    data: any[];
    loading: boolean;
    handleDownload?: (url: string, label: string) => void;
    updateRating?: (id: string, rating: number) => void;
    updateStatus?: (id: string, status: string) => void;
    onSuggestJobOffer?: (talent: any) => void;
    selectable?: boolean;
    selectedTalents?: MultiSelectTalent[];
    onSelectionChange?: (talentId: string, selected: boolean, status?: string) => void;
    isModal?: boolean;
}

const TalentListGrid = ({
    cardMenuList,
    data,
    loading,
    handleDownload,
    updateRating,
    updateStatus,
    onSuggestJobOffer,
    selectable = false,
    selectedTalents = [],
    onSelectionChange,
    isModal = false,
}: TalentListGridProps) => {
    const { open } = useDrawer();

    return (
        <Grid
            container
            spacing={isModal ? 2 : 2}
            minHeight={isModal ? 'auto' : '100%'}
            sx={
                isModal
                    ? {
                        padding: '0',
                        justifyContent: 'flex-start',
                        width: '100%',
                        margin: 0,
                        '& .MuiGrid-item': {
                            display: 'flex',
                            justifyContent: 'center',
                        },
                    }
                    : {}
            }>
            {loading &&
                Array.from(new Array(12)).map((val, index) => (
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={isModal ? 4 : 4}
                        lg={isModal ? 4 : open ? 4 : 3}
                        xl={isModal ? 4 : 3}
                        key={index}>
                        <Card
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '217px',
                                width: isModal ? '100%' : 'auto',
                                minWidth: isModal ? '280px' : '306px',
                                maxWidth: isModal ? '306px' : 'none',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '8px',
                            }}>
                            {/* Header Section */}
                            <Box
                                sx={{
                                    paddingTop: '8.5px',
                                    paddingLeft: '15.53px',
                                    paddingRight: '15.53px',
                                    paddingBottom: '18.5px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}>
                                <Skeleton variant="text" width="120px" height="14px" />
                                <Box sx={{ display: 'flex', gap: '6px' }}>
                                    <Skeleton variant="circular" width="24px" height="24px" />
                                    <Skeleton variant="circular" width="24px" height="24px" />
                                </Box>
                            </Box>

                            {/* Content Section */}
                            <Box
                                sx={{
                                    paddingLeft: '15.53px',
                                    paddingRight: '15.53px',
                                    paddingBottom: '27px',
                                    flex: 1,
                                }}>
                                <Box sx={{ display: 'flex', gap: '18px' }}>
                                    {/* Avatar with status */}
                                    <Box sx={{ position: 'relative', width: 'fit-content' }}>
                                        <Skeleton variant="circular" width="42px" height="42px" />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '36px',
                                                right: '5px',
                                            }}>
                                            <Skeleton variant="rounded" width="31px" height="20px" />
                                        </Box>
                                    </Box>

                                    {/* User info */}
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {/* Name */}
                                        <Skeleton variant="text" width="80%" height="24px" />

                                        {/* Info grid */}
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '4px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Skeleton variant="circular" width="12px" height="12px" />
                                                <Skeleton variant="text" width="50px" height="14px" />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Skeleton variant="circular" width="12px" height="12px" />
                                                <Skeleton variant="text" width="60px" height="14px" />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Skeleton variant="circular" width="12px" height="12px" />
                                                <Skeleton variant="text" width="40px" height="14px" />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Skeleton variant="circular" width="12px" height="12px" />
                                                <Skeleton variant="text" width="70px" height="14px" />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Footer Section */}
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(60, 45, 225, 0.3)',
                                    padding: '8px 12px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Skeleton variant="text" width="10px" height="16px" />
                                        <Skeleton variant="circular" width="16px" height="16px" />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: '4px' }}>
                                        {Array.from(new Array(5)).map((_, starIndex) => (
                                            <Skeleton key={starIndex} variant="circular" width="16px" height="16px" />
                                        ))}
                                    </Box>
                                </Box>
                                <Skeleton variant="text" width="80px" height="14px" />
                            </Box>
                        </Card>
                    </Grid>
                ))}

            {!loading && data?.length === 0 && (
                <Grid item xs={12}>
                    <EmptyState
                        title="Nous n'avons actuellement aucun candidat"
                        subtitle="Commençons par inviter de nouveaux talents à s'inscrire sur Upercut."
                    />
                </Grid>
            )}

            {!loading &&
                data?.length > 0 &&
                data.map(talent => {
                    const filteredMenuList = cardMenuList.filter(val => (talent.active ? val.id !== 4 : val.id !== 3));

                    return (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={isModal ? 4 : 4}
                            lg={isModal ? 4 : open ? 4 : 3}
                            xl={isModal ? 4 : 3}
                            key={talent._id}
                            sx={
                                isModal
                                    ? {
                                        display: 'flex',
                                        justifyContent: 'center',
                                        maxWidth: '33.333%',
                                        flexBasis: '33.333%',
                                        flex: '0 0 auto',
                                    }
                                    : {}
                            }>
                            <TalentCard
                                cardMenuList={filteredMenuList}
                                talent={talent}
                                handleDownload={handleDownload}
                                updateRating={updateRating}
                                updateStatus={updateStatus}
                                onSuggestJobOffer={onSuggestJobOffer}
                                selectable={selectable}
                                selected={selectedTalents.some(selected => selected._id === talent._id)}
                                onSelectionChange={onSelectionChange}
                                isModal={isModal}
                            />
                        </Grid>
                    );
                })}
        </Grid>
    );
};

export default TalentListGrid;
